import { hasStatus, objectEntries } from '@caresend/utils';
import { getRecordManagerByStoreRef } from './getters.js';
import { WorkflowItemStatus } from './model.js';

const getDebugLogger = (log) => (...args) => {
    if (!log)
        return;
    console.info('[WF]:', ...args);
};
const isItemEqual = (item, other) => {
    var _a, _b;
    return item.id === other.id
        && ((_a = item.recordMeta.childWorkflow) === null || _a === void 0 ? void 0 : _a.context) === ((_b = other.recordMeta.childWorkflow) === null || _b === void 0 ? void 0 : _b.context)
        && item.recordMeta.referenceID === other.recordMeta.referenceID
        && item.recordMeta.storeRef === other.recordMeta.storeRef;
};
const getIsItemComplete = (item) => {
    const { status, options } = item;
    return (hasStatus(status, WorkflowItemStatus.COMPLETE)
        || options.disabled);
};
const getIsItemFailed = (item) => hasStatus(item, WorkflowItemStatus.FAILED);
const getIsItemCompleteAndBlocking = (item) => getIsItemComplete(item) && !item.options.nonBlocking;
const getIsNextActionable = (item) => !getIsItemComplete(item) && !item.options.nonBlocking && !getIsItemFailed(item);
const getIsWorkflowPartiallyFailed = (workflow) => workflow.some((item) => hasStatus(item, WorkflowItemStatus.FAILED));
/**
 * Given a workflow and a new child workflow item, return whether the workflow
 * is fully failed.
 */
const getIsWorkflowFailed = (workflow, 
/** Only skipped if the child status is pending */
newChildWorkflowItem, failureThreshold = 'full') => {
    if (failureThreshold === 'partial') {
        if (hasStatus(newChildWorkflowItem, WorkflowItemStatus.FAILED))
            return true;
        return getIsWorkflowPartiallyFailed(workflow);
    }
    return workflow.every((_item) => {
        let item = _item;
        if (newChildWorkflowItem && isItemEqual(item, newChildWorkflowItem)) {
            item = newChildWorkflowItem;
        }
        return hasStatus(item, WorkflowItemStatus.FAILED);
    });
};
/** Collect the next actionable items that are non-blocking for the rest of the workflow */
const getActionableNonBlockingItems = (workflow) => {
    const actionable = [];
    let index = 0;
    let currentItem = workflow[index];
    while (currentItem
        && !getIsItemComplete(currentItem)
        && currentItem.options.nonBlocking) {
        actionable.push(currentItem);
        index += 1;
        currentItem = workflow[index];
    }
    return actionable;
};
const getNextActionable = (workflow) => workflow.find(getIsNextActionable);
const getWorkflowItem = (storeRef, referenceID, context, itemID, store) => {
    var _a;
    const manager = getRecordManagerByStoreRef(storeRef, store);
    if (!manager)
        return null;
    const parent = manager.getRawClone(referenceID);
    if (!parent)
        return null;
    const workflowItem = Object.values((_a = manager.getters.workflows(parent)[context]) !== null && _a !== void 0 ? _a : {})
        .flat()
        .filter(getIsNextActionable)
        .find((item) => item.id === itemID);
    return workflowItem;
};
/**
 * Given information generally related to a workflow item's relationship to a
 * parent, return the workflow that the item points to.
 */
const getWorkflow = (storeRef, referenceID, context, parentID, store) => {
    var _a, _b;
    const manager = getRecordManagerByStoreRef(storeRef, store);
    if (!manager)
        return [];
    const parent = manager.getRaw(referenceID);
    if (!parent)
        return [];
    const workflowsByContext = manager.getters.workflows(parent)[context];
    if (!workflowsByContext)
        return [];
    let workflow = workflowsByContext[context];
    if (!workflow)
        return [];
    const workflowItem = getWorkflowItem(storeRef, referenceID, context, parentID, store);
    const failedContext = (_a = workflowItem === null || workflowItem === void 0 ? void 0 : workflowItem.recordMeta.childWorkflow) === null || _a === void 0 ? void 0 : _a.failureContext;
    if (failedContext && hasStatus(workflowItem, WorkflowItemStatus.FAILED)) {
        workflow = (_b = workflowsByContext[failedContext]) !== null && _b !== void 0 ? _b : workflow;
    }
    return workflow;
};
const isParent = ({ recordMeta: child }, { recordMeta: parent }) => {
    var _a, _b;
    return (((_a = parent.childWorkflow) === null || _a === void 0 ? void 0 : _a.context) === child.parent.context
        || ((_b = parent.childWorkflow) === null || _b === void 0 ? void 0 : _b.failureContext) === child.parent.context)
        && parent.referenceID === child.parent.referenceID;
};
const getParentItemsAndWorkflow = (workflowItem, store, debug = false) => {
    const { recordMeta: { parent } } = workflowItem;
    const defaultReturn = { parents: null, parentWorkflow: null };
    if (!parent)
        return defaultReturn;
    const logger = getDebugLogger(debug);
    const manager = getRecordManagerByStoreRef(parent.storeRef, store);
    if (!manager)
        return defaultReturn;
    let parentWorkflow = null;
    const parents = [];
    const parentWorkflows = manager.getters.workflows(parent.referenceID);
    logger('parentWorkflows', parentWorkflows);
    Object.values(parentWorkflows).forEach((byContext) => {
        Object.values(byContext).forEach((workflow) => {
            const itemsWithRelationship = workflow.filter((item) => isParent(workflowItem, item));
            logger('maybe', itemsWithRelationship);
            if (itemsWithRelationship) {
                parentWorkflow = workflow;
                parents.push(...itemsWithRelationship);
            }
        });
    });
    logger('parentWorkflow', parentWorkflow);
    if (!parentWorkflow)
        return defaultReturn;
    return { parents, parentWorkflow };
};
/**
 * A child workflow is failed if any or all of its items are failed.
 * The `failureThreshold` parameter determines whether the check should be for
 * any failed items (`partial`) or all items (`full`). This applies to a
 * child workflow's items.
 */
const getIsChildWorkflowFailed = (workflowItem, store, newChildWorkflowItem, failureThreshold = 'full', debug = false) => {
    var _a, _b;
    const logger = getDebugLogger(debug);
    const { recordMeta: { referenceID, childWorkflow, storeRef, }, } = workflowItem;
    if (!childWorkflow)
        return false;
    logger('childWorkflow', childWorkflow);
    const manager = getRecordManagerByStoreRef(storeRef, store);
    if (!manager)
        return false;
    const childWorkflows = (_b = (_a = manager.getters).childWorkflows) === null || _b === void 0 ? void 0 : _b.call(_a, referenceID);
    logger('childWorkflows', childWorkflows);
    if (!childWorkflows)
        return false;
    const entries = objectEntries(childWorkflows);
    const { context } = childWorkflow;
    const matches = ([childID, workflows]) => {
        logger('childID', childID);
        logger('workflows', workflows);
        const workflow = workflows[context];
        if (!workflow)
            return true;
        return getIsWorkflowFailed(workflow, newChildWorkflowItem, failureThreshold);
    };
    if (failureThreshold === 'full') {
        return entries.every(matches);
    }
    return entries.some(matches);
};
/**
 * A child workflow is complete if all of its items are complete or failed.
 * A partially failed child workflow requires the sibling workflow of which the
 * failure context defined by the parent points to.
 */
const getIsChildWorkflowComplete = (workflowItem, store, newChildWorkflowItem, useFailureContext = false, debug = false) => {
    var _a, _b;
    const logger = getDebugLogger(debug);
    const { recordMeta: { referenceID, childWorkflow, storeRef, }, } = workflowItem;
    if (!childWorkflow)
        return false;
    logger('childWorkflow', childWorkflow);
    const manager = getRecordManagerByStoreRef(storeRef, store);
    if (!manager)
        return false;
    logger('manager', storeRef, manager);
    const childWorkflows = (_b = (_a = manager.getters).childWorkflows) === null || _b === void 0 ? void 0 : _b.call(_a, referenceID);
    logger('childWorkflows', childWorkflows);
    if (!childWorkflows)
        return false;
    return Object.values(childWorkflows).every((workflows) => {
        var _a;
        const context = useFailureContext
            ? (_a = childWorkflow.failureContext) !== null && _a !== void 0 ? _a : null
            : childWorkflow.context;
        /**
         * There would only be no context found in the event that the `useFailureContext` flag is true
         * but there is no failure context available. This should result in the workflow being considered complete.
         */
        if (!context)
            return true;
        let workflow = workflows[context];
        if (workflow && getIsWorkflowPartiallyFailed(workflow) && childWorkflow.failureContext) {
            workflow = workflows[childWorkflow.failureContext];
        }
        return workflow === null || workflow === void 0 ? void 0 : workflow.every((item) => {
            if (newChildWorkflowItem
                && isItemEqual(item, newChildWorkflowItem)
                && (hasStatus(newChildWorkflowItem, [
                    WorkflowItemStatus.COMPLETE,
                    WorkflowItemStatus.FAILED,
                ])))
                return true;
            if (item.options.nonBlocking)
                return true;
            if (hasStatus(item, WorkflowItemStatus.FAILED))
                return true;
            return getIsItemCompleteAndBlocking(item);
        });
    });
};
const getIsWorkflowItemUnlocked = (workflowItem, store, debug = false) => {
    if (!workflowItem)
        return false;
    const logger = getDebugLogger(debug);
    logger('checking...', workflowItem.recordMeta);
    const { parents, parentWorkflow, } = getParentItemsAndWorkflow(workflowItem, store, debug);
    const parent = parents === null || parents === void 0 ? void 0 : parents[0];
    logger('parent', parent === null || parent === void 0 ? void 0 : parent.recordMeta);
    if (!parentWorkflow || !parent)
        return true;
    const nextParentLevelWorkflowItem = getNextActionable(parentWorkflow);
    logger('nextParentLevelWorkflowItem', nextParentLevelWorkflowItem === null || nextParentLevelWorkflowItem === void 0 ? void 0 : nextParentLevelWorkflowItem.recordMeta);
    if (!nextParentLevelWorkflowItem)
        return true;
    const actionableParentNonBlockingItems = getActionableNonBlockingItems(parentWorkflow);
    logger('actionableParentNonBlockingItems', actionableParentNonBlockingItems);
    if (actionableParentNonBlockingItems.find((item) => isParent(workflowItem, item))) {
        return true;
    }
    logger('nextParentLevelWorkflowItem', nextParentLevelWorkflowItem.recordMeta);
    if (isParent(workflowItem, nextParentLevelWorkflowItem)) {
        return getIsWorkflowItemUnlocked(nextParentLevelWorkflowItem, store, debug);
    }
    return false;
};
const workflowUtils = {
    getIsItemCompleteAndBlocking,
    getActionableNonBlockingItems,
    getIsNextActionable,
    getParentItemsAndWorkflow,
    getNextActionable,
    isItemEqual,
    isParent,
    getIsChildWorkflowComplete,
    getIsWorkflowItemUnlocked,
    getWorkflowItem,
    getIsWorkflowFailed,
    getIsChildWorkflowFailed,
    getWorkflow,
};

export { workflowUtils };
